import { t } from '@trimble/i18n';
import type { LengthUnit } from '@fulcrum/lib';
import { useQueryClient } from '@tanstack/solid-query';
import { createForm } from '@tanstack/solid-form';
import {
  batch,
  Component,
  createMemo,
  createSignal,
  onMount,
  Show,
  untrack,
} from 'solid-js';
import { HeatmapPaletteApiService } from '../heatmap-palette-api-service';
import { parseStopValue } from '@trimble/measurements/volume-heatmap-palette';
import {
  colorAtValue,
  formatMeterQuantityForStopDisplay,
  formatStopValueForDisplay,
  formatStopValueInMeters,
  heatmapHorizontalPreviewCss,
  barPickOffsetYToValueInMeters,
  parseDisplayStopValueToMeters,
  sortStopsByValueDescending,
  stopValuesSemanticallyEqual,
} from '../heatmap-stop-layout';

import {
  EditablePalette,
  EditableStop,
  TextureType,
} from '../types/heatmap.types';
import { useVolumeHeatmap } from '../VolumeHeatmapProvider';
import {
  heatmapPaletteAnalyticsProperties,
  paletteCountAfterWrite,
} from '../heatmap-palette-analytics';
import { NameInput } from './components/NameInput';
import { NewColorStop } from './components/NewColorStop';
import { PaletteStopViewer } from './components/PaletteStopViewer';
import { TextureSelection } from './components/TextureSelection';
import { HeatmapSectionLabel } from '../components/HeatmapSectionLabel';
import { ColorStopsSectionHeader } from './components/ColorStopsSectionHeader';
import { HeatmapPreviewBar } from '../gradient-preview/HeatmapPreviewBar';
import { PanelLayout, Spinner } from '@tcweb/connect-ui';
import {
  ConfirmModus,
  ConfirmModusHandle,
} from '@trimble/ui-components/solidjs';
import {
  getDuplicateStopIds,
  getPaletteNameValidationError,
  getPaletteSaveDisabledReason,
  isDuplicateStopValue,
  isDuplicateStopValueForStop,
  PaletteFormValues,
  PaletteNameValidationError,
  validatePaletteForm,
} from './palette-form-validation';
import {
  buildPaletteNameContext,
  getPaletteSaveDisabledTooltip,
} from './palette-name-context';
import {
  buildEditedPaletteForSave,
  promotePaletteToDefault,
  submitExistingPaletteSave,
} from './palette-set-default-save';
import { resolvePaletteSaveErrorDisplay } from './parse-palette-save-error';
import { createInvalidStopDraftTracker } from './createInvalidStopDraftTracker';
import { EDIT_PALETTE_SET_DEFAULT_CHECKBOX_CSS } from './edit-palette-set-default-checkbox-styles';
import { PALETTE_STOP_NUMBER_INPUT_SPINNER_HIDE_CSS } from './palette-stop-number-input-styles';
import { canAddColorStop, MAX_COLOR_STOP_COUNT } from './palette-stop-limits';

export interface EditPalettePanelProps {
  /** Project length unit used for the boundary value labels. Defaults to 'm'. */
  readonly lengthUnit?: LengthUnit;
  /** Bar click insert + handle drag on the gradient bar (feature-flagged in host apps). */
  readonly enablePaletteBarStopEditing?: boolean;
  onBack(): void;
  onClose(): void;
}

const NEW_PALETTE_DEFAULTS: EditablePalette = {
  id: '',
  name: 'measurements.volumetric.gradient.newPaletteName',
  paletteType: 'CutFill',
  textureType: 'gradient',
  default: false,
  colorAboveRange: '#6A6E79',
  colorBelowRange: '#6A6E79',
  stops: [
    { id: 'cut', color: '#E53E3E', value: '2.000' },
    { id: 'net', color: '#FFFFFF', value: '0' },
    { id: 'fill', color: '#3182CE', value: '-2.000' },
  ],
};

/** The measurements API requires a palette to keep at least two stops. */
const MIN_STOP_COUNT = 2;

function buildInitialFormValues(palette: EditablePalette): PaletteFormValues {
  return {
    name: t(palette.name),
    textureType: palette.textureType,
    colorAboveRange: palette.colorAboveRange,
    colorBelowRange: palette.colorBelowRange,
    stops: palette.stops.map((stop) => ({ ...stop })),
    markDefault: palette.default,
  };
}

function paletteFormValuesEqual(
  left: PaletteFormValues,
  right: PaletteFormValues,
  displayUnit: LengthUnit
): boolean {
  return (
    left.name === right.name &&
    left.textureType === right.textureType &&
    left.colorAboveRange === right.colorAboveRange &&
    left.colorBelowRange === right.colorBelowRange &&
    left.markDefault === right.markDefault &&
    left.stops.length === right.stops.length &&
    left.stops.every(
      (stop, index) =>
        stop.id === right.stops[index]?.id &&
        stop.color === right.stops[index]?.color &&
        stopValuesSemanticallyEqual(
          stop.value,
          right.stops[index]?.value ?? '',
          displayUnit
        )
    )
  );
}

export const EditPalettePanel: Component<EditPalettePanelProps> = (props) => {
  const volumeHeatmap = useVolumeHeatmap();
  const palette =
    untrack(() => volumeHeatmap.selectedPalette()) ?? NEW_PALETTE_DEFAULTS;
  const isNewPalette = palette.id === '';
  const [initialFormValues, setInitialFormValues] = createSignal(
    buildInitialFormValues(palette)
  );

  const queryClient = useQueryClient();
  const apiService = new HeatmapPaletteApiService(queryClient);
  const setDefaultMutation = apiService.setDefaultPaletteMutation();
  const deleteMutation = apiService.deletePaletteMutation();

  const [isSaving, setIsSaving] = createSignal(false);
  const [saveError, setSaveError] = createSignal<string | undefined>();
  const [attemptedOverLimit, setAttemptedOverLimit] = createSignal(false);
  const [attemptedDuplicate, setAttemptedDuplicate] = createSignal(false);
  const [activeStopId, setActiveStopId] = createSignal<string | null>(null);
  const [focusStopId, setFocusStopId] = createSignal<string | null>(null);
  const [draggingStopDisplayValue, setDraggingStopDisplayValue] = createSignal<
    string | null
  >(null);
  const [serverNameError, setServerNameError] = createSignal<
    PaletteNameValidationError | undefined
  >();
  const invalidStopDrafts = createInvalidStopDraftTracker();
  let deleteConfirmModal!: ConfirmModusHandle;

  const lengthUnit = (): LengthUnit => props.lengthUnit ?? 'm';

  const paletteBarStopEditingEnabled = (): boolean =>
    props.enablePaletteBarStopEditing === true;

  const paletteNameContext = createMemo(() =>
    buildPaletteNameContext({
      availablePalettes: volumeHeatmap.availablePalettes(),
      currentPaletteId: isNewPalette ? undefined : palette.id,
      currentPaletteType: palette.paletteType,
    })
  );

  function validatePaletteNameField(
    name: string
  ): PaletteNameValidationError | undefined {
    return getPaletteNameValidationError(name, paletteNameContext());
  }

  const paletteForm = createForm(() => ({
    defaultValues: initialFormValues(),
    validators: {
      onChange: ({ value }) =>
        validatePaletteForm(value, lengthUnit(), paletteNameContext()),
      onMount: ({ value }) =>
        validatePaletteForm(value, lengthUnit(), paletteNameContext()),
    },
    onSubmit: ({ value }) => {
      submitPalette(value);
    },
  }));

  const formValues = paletteForm.useStore((state) => state.values);
  const isFormDirty = createMemo(
    () =>
      !paletteFormValuesEqual(formValues(), initialFormValues(), lengthUnit())
  );
  const duplicateStopIds = createMemo(() =>
    getDuplicateStopIds(formValues().stops, lengthUnit())
  );

  onMount(() => {
    resetMutationErrors();
  });

  function resetTransientMutationErrors(): void {
    setSaveError(undefined);
    setDefaultMutation.reset();
    deleteMutation.reset();
  }

  function resetMutationErrors(): void {
    resetTransientMutationErrors();
    setServerNameError(undefined);
  }

  function resolveNameFieldError(
    name: string,
    isBlurred: boolean
  ): PaletteNameValidationError | undefined {
    const fromServer = serverNameError();
    if (fromServer !== undefined) {
      return fromServer;
    }

    if (!isBlurred) {
      return undefined;
    }

    return validatePaletteNameField(name);
  }

  const deleteError = createMemo(() =>
    deleteMutation.isError
      ? t('measurements.volumetric.gradient.deleteError')
      : undefined
  );

  const isPersistedDefault = (): boolean => palette.default;

  const showDefaultChip = createMemo(() =>
    isNewPalette ? formValues().markDefault : palette.default
  );

  const hasContentChanges = createMemo(() => {
    const values = formValues();
    const baseline = initialFormValues();
    return !paletteFormValuesEqual(
      { ...values, markDefault: baseline.markDefault },
      baseline,
      lengthUnit()
    );
  });

  function syncPersistedContentBaseline(values: PaletteFormValues): void {
    const baseline = initialFormValues();
    setInitialFormValues({
      name: values.name,
      textureType: values.textureType,
      colorAboveRange: values.colorAboveRange,
      colorBelowRange: values.colorBelowRange,
      stops: values.stops.map((stop) => ({ ...stop })),
      markDefault: baseline.markDefault,
    });
  }

  const saveDisabledReason = createMemo(() => {
    const serverError = serverNameError();
    if (serverError !== undefined) {
      return serverError;
    }

    return getPaletteSaveDisabledReason({
      isSaving: isSaving(),
      values: formValues(),
      isDirty: isFormDirty(),
      isNewPalette,
      displayUnit: lengthUnit(),
      nameContext: paletteNameContext(),
      hasInvalidStopDrafts: invalidStopDrafts.hasInvalidStopDrafts(),
    });
  });

  const canSave = createMemo(() => saveDisabledReason() === undefined);

  const saveDisabledTooltip = createMemo((): string => {
    const reason = saveDisabledReason();
    return reason === undefined ? '' : getPaletteSaveDisabledTooltip(reason);
  });

  const existingValuesInMeters = createMemo(() =>
    formValues().stops.map((stop) => parseStopValue(stop.value))
  );

  const previewPalette = createMemo<EditablePalette>(() => {
    const values = formValues();
    return {
      ...palette,
      name: values.name,
      stops: values.stops,
      textureType: values.textureType,
      colorAboveRange: values.colorAboveRange,
      colorBelowRange: values.colorBelowRange,
    };
  });

  const deleteConfirmPreviewCss = createMemo(() => {
    const preview = previewPalette();
    return heatmapHorizontalPreviewCss(preview.stops, preview.textureType, {
      above: preview.colorAboveRange,
      below: preview.colorBelowRange,
    });
  });

  const maxStopsWarning = createMemo(() => {
    if (!attemptedOverLimit() || canAddColorStop(formValues().stops.length)) {
      return undefined;
    }
    return t('measurements.volumetric.gradient.maxColorStopsError', {
      maxColorStops: MAX_COLOR_STOP_COUNT.toString(),
    });
  });

  const duplicateStopWarning = createMemo(() => {
    if (!attemptedDuplicate()) {
      return undefined;
    }
    return t('measurements.volumetric.gradient.newStopDuplicateValue');
  });

  function handleAddStop(
    valueInMeters: number,
    color?: string,
    options?: { focusAfterInsert?: boolean; focusValueInput?: boolean }
  ): string | undefined {
    const currentStops = paletteForm.state.values.stops;
    const newStop: EditableStop = {
      id: crypto.randomUUID(),
      color: color ?? colorAtValue(valueInMeters, currentStops),
      value: formatStopValueInMeters(valueInMeters, lengthUnit()),
    };
    paletteForm.setFieldValue(
      'stops',
      sortStopsByValueDescending([...currentStops, newStop])
    );
    if (options?.focusAfterInsert) {
      setActiveStopId(newStop.id);
      if (options.focusValueInput !== false) {
        setFocusStopId(newStop.id);
      }
    }
    return newStop.id;
  }

  function handleManualAddStop(
    valueInMeters: number,
    color?: string,
    options?: { focusAfterInsert?: boolean; focusValueInput?: boolean }
  ): void {
    batch(() => {
      setAttemptedOverLimit(false);
      setAttemptedDuplicate(false);
      handleAddStop(valueInMeters, color, options);
    });
  }

  function handleBarInsertStop(valueInMeters: number): void {
    const currentStops = paletteForm.state.values.stops;
    if (!canAddColorStop(currentStops.length)) {
      handleStopLimitReached();
      return;
    }
    if (
      isDuplicateStopValue(
        valueInMeters,
        existingValuesInMeters(),
        lengthUnit()
      )
    ) {
      setAttemptedDuplicate(true);
      return;
    }
    handleManualAddStop(valueInMeters, undefined, { focusAfterInsert: true });
  }

  function handleAddColorStopAtBarTop(): void {
    const currentStops = paletteForm.state.values.stops;
    if (!canAddColorStop(currentStops.length)) {
      handleStopLimitReached();
      return;
    }
    const valueInMeters = barPickOffsetYToValueInMeters(
      0,
      currentStops,
      formValues().textureType
    );
    if (
      isDuplicateStopValue(
        valueInMeters,
        existingValuesInMeters(),
        lengthUnit()
      )
    ) {
      setAttemptedDuplicate(true);
      return;
    }
    handleManualAddStop(valueInMeters, undefined, {
      focusAfterInsert: true,
      focusValueInput: false,
    });
  }

  const addColorStopAtTopDisabled = createMemo(
    () => isSaving() || !canAddColorStop(formValues().stops.length)
  );

  const addColorStopAtTopTooltip = createMemo((): string => {
    if (!canAddColorStop(formValues().stops.length)) {
      return t('measurements.volumetric.gradient.maxColorStopsError', {
        maxColorStops: MAX_COLOR_STOP_COUNT.toString(),
      });
    }
    return t('measurements.volumetric.gradient.addColorStopAtTop');
  });

  function handleStopLimitReached(): void {
    batch(() => {
      setAttemptedDuplicate(false);
      setAttemptedOverLimit(true);
    });
  }

  function handleClearActiveStop(): void {
    batch(() => {
      setActiveStopId(null);
      setFocusStopId(null);
    });
  }

  function handleStopActivate(stopId: string): void {
    batch(() => {
      setActiveStopId(stopId);
      setFocusStopId(null);
    });
  }

  function handleStopDragDraft(stopId: string, valueInMeters: number): void {
    batch(() => {
      setActiveStopId(stopId);
      setDraggingStopDisplayValue(
        formatMeterQuantityForStopDisplay(valueInMeters, lengthUnit())
      );
    });
  }

  function handleStopDragCommit(stopId: string, valueInMeters: number): void {
    if (
      isDuplicateStopValueForStop(
        valueInMeters,
        stopId,
        paletteForm.state.values.stops,
        lengthUnit()
      )
    ) {
      batch(() => {
        setDraggingStopDisplayValue(null);
        setAttemptedDuplicate(true);
      });
      return;
    }
    batch(() => {
      setDraggingStopDisplayValue(null);
      setAttemptedDuplicate(false);
    });
    const stored = formatStopValueInMeters(valueInMeters, lengthUnit());
    handleStopValueCommit(
      stopId,
      formatStopValueForDisplay(stored, lengthUnit()),
      { clearActiveStop: false }
    );
    setActiveStopId(stopId);
  }

  function draggingStopDisplayValueFor(stopId: string): string | undefined {
    if (draggingStopDisplayValue() === null || activeStopId() !== stopId) {
      return undefined;
    }
    return draggingStopDisplayValue() ?? undefined;
  }

  function parseNewStopToMeters(display: string): number | null {
    return parseDisplayStopValueToMeters(display, lengthUnit());
  }

  function handleStopValueCommit(
    id: string,
    displayValue: string,
    options?: { clearActiveStop?: boolean }
  ): void {
    const clearActiveStop = options?.clearActiveStop ?? true;
    batch(() => {
      if (clearActiveStop) {
        setActiveStopId((current) => (current === id ? null : current));
        setFocusStopId((current) => (current === id ? null : current));
      }
      setDraggingStopDisplayValue(null);
    });

    const meters = parseDisplayStopValueToMeters(displayValue, lengthUnit());
    if (meters === null) return;

    const currentStops = paletteForm.state.values.stops;
    const currentStop = currentStops.find((stop) => stop.id === id);
    if (!currentStop) return;

    const formatted = formatStopValueInMeters(meters, lengthUnit());
    if (formatted === currentStop.value) return;

    const updated = currentStops.map((stop) =>
      stop.id === id ? { ...stop, value: formatted } : stop
    );

    if (
      stopValuesSemanticallyEqual(formatted, currentStop.value, lengthUnit())
    ) {
      paletteForm.setFieldValue('stops', updated);
      return;
    }

    paletteForm.setFieldValue('stops', sortStopsByValueDescending(updated));
  }

  function handleRemoveStop(id: string): void {
    const currentStops = paletteForm.state.values.stops;
    if (currentStops.length <= MIN_STOP_COUNT) return;

    invalidStopDrafts.clearStopDraftValidity(id);

    paletteForm.setFieldValue(
      'stops',
      currentStops.filter((stop) => stop.id !== id)
    );
  }

  function handleStopColorChange(id: string, color: string): void {
    const currentStops = paletteForm.state.values.stops;
    const index = currentStops.findIndex((stop) => stop.id === id);
    if (index === -1) return;

    const updated = currentStops.map((stop, stopIndex) =>
      stopIndex === index ? { ...stop, color } : stop
    );
    paletteForm.setFieldValue('stops', updated);
  }

  function handleAboveColorChange(color: string): void {
    paletteForm.setFieldValue('colorAboveRange', color);
  }

  function handleBelowColorChange(color: string): void {
    paletteForm.setFieldValue('colorBelowRange', color);
  }

  function handleTextureTypeChange(textureType: TextureType): void {
    paletteForm.setFieldValue('textureType', textureType);
  }

  function handleBack(): void {
    if (isSaving()) return;
    volumeHeatmap.cancelPaletteEdit();
    props.onBack();
  }

  function handleClose(): void {
    if (isSaving()) return;
    volumeHeatmap.cancelPaletteEdit();
    props.onClose();
  }

  function handleDelete(): void {
    if (isSaving() || isPersistedDefault()) return;
    deleteConfirmModal?.show();
  }

  async function handleDeleteConfirm(): Promise<void> {
    if (isSaving() || isPersistedDefault()) return;
    resetMutationErrors();
    deleteMutation.mutate(
      {
        paletteId: palette.id,
        tenantTrn: volumeHeatmap.tenantTrn(),
      },
      {
        onSuccess: () => {
          volumeHeatmap.reportPaletteAnalytics({
            action: 'deleted',
            ...heatmapPaletteAnalyticsProperties(
              palette,
              paletteCountAfterWrite(
                volumeHeatmap.availablePalettes(),
                palette.id,
                'delete'
              )
            ),
          });
          if (volumeHeatmap.selectedPaletteId() === palette.id) {
            volumeHeatmap.selectDefaultPalette();
          }
          props.onBack();
        },
      }
    );
  }

  function handleSave(): void {
    if (!canSave()) return;
    submitPalette(formValues());
  }

  function completePaletteSave(): void {
    batch(() => {
      setIsSaving(false);
      volumeHeatmap.completePaletteEdit();
      props.onBack();
    });
  }

  function handleSaveError(error?: unknown): void {
    const display = resolvePaletteSaveErrorDisplay(
      error,
      t('measurements.volumetric.gradient.saveError')
    );

    batch(() => {
      setIsSaving(false);
      setServerNameError(display.nameError);
      setSaveError(display.bannerMessage);
    });
  }

  function promoteToDefaultOnSave(): void {
    resetMutationErrors();
    promotePaletteToDefault({
      palette,
      tenantTrn: volumeHeatmap.tenantTrn(),
      mutation: setDefaultMutation,
      onSuccess: () => {
        volumeHeatmap.reportPaletteAnalytics({
          action: 'setAsDefault',
          ...heatmapPaletteAnalyticsProperties(
            {
              stops: formValues().stops,
              textureType: formValues().textureType,
              default: true,
            },
            paletteCountAfterWrite(
              volumeHeatmap.availablePalettes(),
              palette.id,
              'unchanged'
            ),
            true
          ),
        });
        completePaletteSave();
      },
      onError: () => {
        batch(() => {
          setIsSaving(false);
          setSaveError(t('measurements.volumetric.gradient.setAsDefaultError'));
        });
      },
    });
  }

  function submitPalette(values: PaletteFormValues): void {
    batch(() => {
      setSaveError(undefined);
      setIsSaving(true);
    });

    if (isNewPalette) {
      volumeHeatmap.savePalette(
        buildEditedPaletteForSave(palette, values, true),
        {
          onSuccess: () => {
            completePaletteSave();
          },
          // Keep the editor open with the draft intact so the user can retry; the
          // selection is untouched by a failed write, so nothing phantom is left.
          onError: (error) => {
            handleSaveError(error);
          },
        }
      );
      return;
    }

    submitExistingPaletteSave({
      values,
      palette,
      hasContentChanges: hasContentChanges(),
      savePalette: (edited, callbacks) =>
        volumeHeatmap.savePalette(edited, callbacks),
      buildEditedPalette: (formValues) =>
        buildEditedPaletteForSave(palette, formValues, false),
      onContentSaved: syncPersistedContentBaseline,
      promoteToDefault: promoteToDefaultOnSave,
      completeSave: completePaletteSave,
      onPaletteUpdateError: handleSaveError,
    });
  }

  function handleMarkDefaultChange(): void {
    if (isSaving() || palette.default) return;
    resetTransientMutationErrors();
    paletteForm.setFieldValue('markDefault', !formValues().markDefault);
  }

  return (
    <div class="flex flex-col h-full" data-testid="edit-palette-panel">
      <style>{PALETTE_STOP_NUMBER_INPUT_SPINNER_HIDE_CSS}</style>
      <style>{EDIT_PALETTE_SET_DEFAULT_CHECKBOX_CSS}</style>
      <PanelLayout
        panelTitle={
          volumeHeatmap.editMode() == 'edit'
            ? t('measurements.volumetric.gradient.editPaletteTitle')
            : t('measurements.volumetric.gradient.addPaletteTitle')
        }
        leftActions={
          <modus-wc-tooltip
            content={t('general.back')}
            position="bottom"
            class="[&_div]:flex"
          >
            <modus-wc-button
              color="tertiary"
              shape="circle"
              size="md"
              type="button"
              variant="borderless"
              // @ts-expect-error -- modus types do not support bool:disabled syntax
              bool:disabled={isSaving()}
              data-testid="edit-palette-back"
              on:buttonClick={handleBack}
            >
              <modus-wc-icon
                name="arrow_back"
                class="text-[var(--modus-wc-color-base-content,#171c1e)]"
              />
            </modus-wc-button>
          </modus-wc-tooltip>
        }
        rightActions={
          <>
            <Show when={!isNewPalette}>
              <modus-wc-tooltip
                content={t(
                  'measurements.volumetric.gradient.deleteDefaultTooltip'
                )}
                position="bottom"
                class="[&_div]:flex"
                disabled={!isPersistedDefault()}
              >
                <modus-wc-button
                  color="tertiary"
                  shape="circle"
                  size="md"
                  type="button"
                  variant="borderless"
                  // @ts-expect-error -- modus types do not support bool:disabled syntax
                  bool:disabled={isSaving() || isPersistedDefault()}
                  data-testid="edit-palette-delete"
                  on:buttonClick={() => void handleDelete()}
                >
                  <modus-wc-icon name="delete" size="md" />
                </modus-wc-button>
              </modus-wc-tooltip>
            </Show>
            <modus-wc-tooltip
              content={t('general.close')}
              position="bottom"
              class="[&_div]:flex"
            >
              <modus-wc-button
                color="tertiary"
                shape="circle"
                size="md"
                type="button"
                variant="borderless"
                // @ts-expect-error -- modus types do not support bool:disabled syntax
                bool:disabled={isSaving()}
                data-testid="edit-palette-close"
                onClick={handleClose}
              >
                <modus-wc-icon
                  name="close"
                  size="md"
                  class="text-[var(--modus-wc-color-base-content,#171c1e)]"
                />
              </modus-wc-button>
            </modus-wc-tooltip>
          </>
        }
        content={
          <>
            <section class="relative flex flex-col h-full">
              <Show when={isSaving()}>
                <div
                  class="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-gray-900/80"
                  data-testid="edit-palette-saving-overlay"
                  aria-busy="true"
                  aria-label={t('general.loading')}
                >
                  <Spinner
                    show={true}
                    class="!min-h-0 !h-auto !max-w-none !m-0 [&_.center_.icon-font]:!size-12 [&_.center_.icon-font]:!bg-[length:3rem_3rem] [&_.center_.icon-font]:!bg-center [&_.center_.icon-font]:!bg-no-repeat"
                  />
                </div>
              </Show>
              <div class="flex flex-col grow p-lg overflow-y-auto flex-1">
                <div class="flex flex-col gap-4">
                  <div class="flex flex-col gap-3">
                    <paletteForm.Field
                      name="name"
                      validators={{
                        onChange: ({ value }) =>
                          validatePaletteNameField(value),
                        onBlur: ({ value }) => validatePaletteNameField(value),
                      }}
                    >
                      {(field) => (
                        <NameInput
                          name={field().state.value}
                          onNameChange={(name) => {
                            setServerNameError(undefined);
                            field().handleChange(name);
                          }}
                          onBlur={field().handleBlur}
                          showDefaultChip={showDefaultChip()}
                          nameError={resolveNameFieldError(
                            field().state.value,
                            field().state.meta.isBlurred
                          )}
                        />
                      )}
                    </paletteForm.Field>

                    <div class="flex items-center gap-3">
                      <modus-wc-checkbox
                        class="edit-palette-set-default-checkbox shrink-0"
                        size="sm"
                        inputId="edit-palette-set-default"
                        attr:aria-label={t(
                          'measurements.volumetric.gradient.setAsDefault'
                        )}
                        value={formValues().markDefault}
                        // @ts-expect-error -- modus types do not support bool:disabled syntax
                        bool:disabled={isSaving() || palette.default}
                        data-testid="edit-palette-set-default"
                        on:inputChange={handleMarkDefaultChange}
                      />
                      <span
                        class={`text-sm text-gray-6 select-none${
                          isSaving() || palette.default ? '' : ' cursor-pointer'
                        }`}
                        onClick={handleMarkDefaultChange}
                      >
                        {t('measurements.volumetric.gradient.setAsDefault')}
                      </span>
                    </div>
                  </div>

                  <Show when={deleteError()}>
                    <div data-testid="edit-palette-delete-error">
                      <modus-wc-alert
                        attr:alert-description={deleteError() ?? ''}
                        role="alert"
                        variant="error"
                      />
                    </div>
                  </Show>
                </div>

                <div class="py-lg" data-testid="edit-palette-section-divider">
                  <div class="border-t border-t-gray-0" role="separator" />
                </div>

                <section
                  class="flex flex-col gap-1"
                  data-testid="edit-palette-color-stops"
                >
                  <Show
                    when={paletteBarStopEditingEnabled()}
                    fallback={
                      <HeatmapSectionLabel
                        uppercase
                        label={t('measurements.volumetric.gradient.colors')}
                      />
                    }
                  >
                    <ColorStopsSectionHeader
                      addStopDisabled={addColorStopAtTopDisabled()}
                      addStopTooltip={addColorStopAtTopTooltip()}
                      onAddStop={handleAddColorStopAtBarTop}
                    />
                  </Show>

                  <Show when={maxStopsWarning()}>
                    <div data-testid="new-color-stop-max-stops-warning">
                      <modus-wc-alert
                        attr:alert-description={maxStopsWarning() ?? ''}
                        role="alert"
                        variant="warning"
                      />
                    </div>
                  </Show>

                  <Show when={duplicateStopWarning()}>
                    <div data-testid="new-color-stop-duplicate-warning">
                      <modus-wc-alert
                        attr:alert-description={duplicateStopWarning() ?? ''}
                        role="alert"
                        variant="error"
                      />
                    </div>
                  </Show>

                  <Show when={!paletteBarStopEditingEnabled()}>
                    <NewColorStop
                      lengthUnit={lengthUnit()}
                      existingValuesInMeters={existingValuesInMeters()}
                      parseToMeters={parseNewStopToMeters}
                      onAddStop={handleManualAddStop}
                      onStopLimitReached={handleStopLimitReached}
                    />
                  </Show>

                  <PaletteStopViewer
                    palette={previewPalette()}
                    lengthUnit={lengthUnit()}
                    editable
                    duplicateStopIds={duplicateStopIds()}
                    activeStopId={
                      paletteBarStopEditingEnabled() ? activeStopId() : null
                    }
                    focusStopId={
                      paletteBarStopEditingEnabled() ? focusStopId() : null
                    }
                    draggingStopDisplayValueById={
                      paletteBarStopEditingEnabled()
                        ? (stopId) => draggingStopDisplayValueFor(stopId)
                        : undefined
                    }
                    onBarInsertStop={
                      paletteBarStopEditingEnabled()
                        ? handleBarInsertStop
                        : undefined
                    }
                    onStopDragDraft={
                      paletteBarStopEditingEnabled()
                        ? handleStopDragDraft
                        : undefined
                    }
                    onStopDragCommit={
                      paletteBarStopEditingEnabled()
                        ? handleStopDragCommit
                        : undefined
                    }
                    onStopActivate={
                      paletteBarStopEditingEnabled()
                        ? handleStopActivate
                        : undefined
                    }
                    onClearActiveStop={
                      paletteBarStopEditingEnabled()
                        ? handleClearActiveStop
                        : undefined
                    }
                    onStopValueCommit={handleStopValueCommit}
                    onStopDraftValidityChange={
                      invalidStopDrafts.handleStopDraftValidityChange
                    }
                    onRemoveStop={handleRemoveStop}
                    onStopColorChange={handleStopColorChange}
                    onAboveColorChange={handleAboveColorChange}
                    onBelowColorChange={handleBelowColorChange}
                  />
                </section>

                <div class="py-lg" data-testid="edit-palette-texture-divider">
                  <div class="border-t border-t-gray-0" role="separator" />
                </div>
                <TextureSelection
                  setTextureType={handleTextureTypeChange}
                  stops={formValues().stops}
                  textureType={formValues().textureType}
                  colorAboveRange={formValues().colorAboveRange}
                  colorBelowRange={formValues().colorBelowRange}
                />
              </div>

              <Show when={saveError()}>
                <div
                  class="px-lg pb-2 flex-shrink-0"
                  data-testid="edit-palette-save-error"
                >
                  <modus-wc-alert
                    attr:alert-description={saveError() ?? ''}
                    role="alert"
                    variant="error"
                  />
                </div>
              </Show>

              <div class="flex items-center justify-end gap-2 px-lg py-3 border-t border-t-gray-0 flex-shrink-0">
                <modus-wc-tooltip
                  content={saveDisabledTooltip()}
                  position="bottom"
                  class="[&_div]:flex"
                  disabled={canSave()}
                >
                  <modus-wc-button
                    color="primary"
                    shape="rectangle"
                    size="md"
                    type="button"
                    variant="filled"
                    // @ts-expect-error -- modus types do not support bool:disabled syntax
                    bool:disabled={!canSave()}
                    data-testid="edit-palette-save"
                    onClick={handleSave}
                  >
                    {t('general.save')}
                  </modus-wc-button>
                </modus-wc-tooltip>
              </div>
            </section>
          </>
        }
      />

      <ConfirmModus
        id="edit-palette-delete-confirm-modal"
        modalRef={(handle) => {
          deleteConfirmModal = handle;
        }}
        header={`${t('general.delete')} ${formValues().name}?`}
        primaryButtonLabel={t('general.delete')}
        primaryButtonClass="danger"
        secondaryButtonLabel={t('general.cancel')}
        onConfirm={() => {
          void handleDeleteConfirm();
        }}
        onCancel={() => undefined}
      >
        <div class="flex flex-col gap-4">
          <HeatmapPreviewBar
            stops={previewPalette().stops}
            backgroundCss={deleteConfirmPreviewCss()}
            testId="edit-palette-delete-confirm-preview"
          />
          <p class="m-0">
            {t('measurements.volumetric.gradient.deleteConfirmDescription')}
          </p>
        </div>
      </ConfirmModus>
    </div>
  );
};
